import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Routes where the bar should appear. Anywhere else (homepage, contact,
// thank-you, wildlife) we leave it off so the visitor isn't nagged where
// the page already has obvious CTAs of its own.
const SHOW_ON_ROUTES = [
  '/birding',
  '/itinerary',
  '/lodge',
  '/transport',
  '/services',
  '/guide',
  '/poasvolcano',
  '/poasdoka',
  '/poasmirador',
  '/doka',
  '/sarchi',
  '/waterfall',
  '/mirador',
];

// IDs of inline TourInquiryForm sections — when one of these is visible
// on screen we hide the sticky bar to avoid stacking two forms/buttons.
const FORM_IDS = [
  'tour_inquiry_birding-form',
  'tour_inquiry_transport-form',
  'tour_inquiry_lodge-form',
];

/**
 * Mobile-only sticky CTA bar. Two thumb-reach buttons: Inquire (scrolls to
 * the inline form on this page, or routes to /birding if no form is here)
 * and WhatsApp. Auto-hides when the inquiry form is already visible.
 */
const MobileStickyCTA = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);
  const observerRef = useRef(null);

  // Observe form visibility — if the form is already on screen, hide the
  // bar so we're not asking the user to "Inquire" while they're literally
  // looking at the form.
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    const targets = FORM_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (!targets.length) {
      setHidden(false);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const anyVisible = entries.some((entry) => entry.isIntersecting);
        setHidden(anyVisible);
      },
      { threshold: 0.1 }
    );
    targets.forEach((el) => observer.observe(el));
    observerRef.current = observer;
    return () => observer.disconnect();
  }, [location.pathname]);

  if (!SHOW_ON_ROUTES.includes(location.pathname)) return null;

  const handleInquireClick = (e) => {
    // If a form exists on this page, smooth-scroll to it. Otherwise route
    // to /birding (the page with the most flexible form).
    const targets = FORM_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (targets.length) {
      e.preventDefault();
      targets[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      e.preventDefault();
      navigate('/birding');
    }
  };

  return (
    <div
      className={`md:hidden fixed bottom-0 left-0 right-0 z-40 bg-gray-900/95 backdrop-blur-sm border-t border-green-500/30 shadow-2xl transition-transform duration-300 ${
        hidden ? 'translate-y-full' : 'translate-y-0'
      }`}
      role='complementary'
      aria-label='Quick contact'
    >
      <div className='flex gap-2 px-3 py-3 max-w-md mx-auto'>
        <a
          href='#tour_inquiry_birding-form'
          onClick={handleInquireClick}
          className='flex-1 text-center py-3 px-4 rounded-full font-bold text-sm shadow-lg text-white'
          style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)' }}
        >
          ✉️ Inquire
        </a>
        <a
          href='https://wa.me/50685499997?text=Hi%20there!%20I%20am%20interested%20in%20your%20services.'
          target='_blank'
          rel='noopener noreferrer'
          className='flex-1 text-center py-3 px-4 rounded-full font-bold text-sm shadow-lg text-white bg-green-600 hover:bg-green-700'
        >
          💬 WhatsApp
        </a>
      </div>
    </div>
  );
};

export default MobileStickyCTA;
